import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import { checkTaskStatus } from "@/lib/task-checker";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
    Video,
    Image as ImageIcon,
    User,
    Eraser,
    Sparkles,
    Loader2,
    Download,
    Coins,
    History,
    Clock,
    RefreshCw,
    Trash2,
    Copy,
    Link as LinkIcon,
    CheckCircle2,
    AlertTriangle,
    Upload,
    ArrowRight,
    Repeat,
    Play,
    X,
    Plus,
    HelpCircle,
    ExternalLink,
    AtSign,
    FastForward,
    Stamp,
    RotateCcw,
    Zap
} from "lucide-react";
import { characterService, AICharacter } from "@/lib/character-service";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from "@/components/ui/dialog";

// Error translations for Thai
const ERROR_TRANSLATIONS: Record<string, string> = {
    // OpenAI/Sora guardrails
    "This content may violate OpenAI's guardrails around violence.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับความรุนแรง",
    "This content may violate OpenAI's guardrails around nudity.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับภาพเปลือย",
    "This content may violate OpenAI's guardrails around sexual content.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับเนื้อหาทางเพศ",
    "This content may violate OpenAI's guardrails.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI",
    // Generic errors
    "Generation failed": "การสร้างล้มเหลว",
    "Task failed at submission - no task ID was created": "งานล้มเหลวตอนส่ง - ไม่มี Task ID ถูกสร้าง",
    "Request timeout - task took too long to complete": "หมดเวลาคำขอ - งานใช้เวลานานเกินไป",
    // Character specific
    "The video of creating character fetch failed. Check access settings or use our File Upload API instead.": "ดึงวิดีโอตัวละครไม่สำเร็จ - กรุณาตรวจสอบวิดีโอที่ใช้สร้างตัวละคร หรือลองอัปโหลดใหม่",
    // Server/Service errors
    "Sora official service is currently under heavy load and not responding. Please try again later.": "เซิร์ฟเวอร์ Sora มีโหลดสูง กรุณาลองใหม่ภายหลัง",
};

// Function to translate error message
const translateErrorMessage = (errorMessage: string, isThaiMode: boolean): string => {
    if (!isThaiMode) return errorMessage;

    // Check exact match first
    if (ERROR_TRANSLATIONS[errorMessage]) {
        return ERROR_TRANSLATIONS[errorMessage];
    }

    // Check partial match
    for (const [eng, thai] of Object.entries(ERROR_TRANSLATIONS)) {
        if (errorMessage.toLowerCase().includes(eng.toLowerCase())) {
            return thai;
        }
    }

    // Check for keywords and provide generic translations
    const lowered = errorMessage.toLowerCase();
    if (lowered.includes("guardrail") || lowered.includes("policy") || lowered.includes("violat")) {
        return `ละเมิดกฎความปลอดภัย: ${errorMessage}`;
    }
    if (lowered.includes("violence") || lowered.includes("violent")) {
        return `เนื้อหามีความรุนแรง: ${errorMessage}`;
    }
    if (lowered.includes("nudity") || lowered.includes("naked") || lowered.includes("sexual")) {
        return `เนื้อหาไม่เหมาะสม: ${errorMessage}`;
    }

    return errorMessage;
};

const Sora = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useLanguage();
    const { user, profile, loading: authLoading } = useAuth();
    const { toast } = useToast();
    const { generate, isGenerating, generationProgress, statusMessage } = useGeneration();

    const [activeTab, setActiveTab] = useState(type || "text-to-video");
    const [promptText, setPromptText] = useState("");
    const [characterPrompt, setCharacterPrompt] = useState("");
    const [safetyInstruction, setSafetyInstruction] = useState("");
    const [selectedRatio, setSelectedRatio] = useState("9:16");
    const [nFrames, setNFrames] = useState("15");
    const [autoRetry, setAutoRetry] = useState(false);
    const [referenceImage, setReferenceImage] = useState<File | null>(null);
    const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
    const [firstFrame, setFirstFrame] = useState<File | null>(null);
    const [lastFrame, setLastFrame] = useState<File | null>(null);
    const [referenceImages, setReferenceImages] = useState<File[]>([]);
    const [referenceVideos, setReferenceVideos] = useState<File[]>([]);
    const [referenceAudios, setReferenceAudios] = useState<File[]>([]);
    const [generateAudio, setGenerateAudio] = useState(true);
    const [characterVideo, setCharacterVideo] = useState<File | null>(null);
    const [characterVideoUrl, setCharacterVideoUrl] = useState<string | null>(null);

    // Alias Mapping States
    const [savedCharacters, setSavedCharacters] = useState<AICharacter[]>([]);
    const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
    const [aliasToSave, setAliasToSave] = useState<{ characterId: string, logId: string } | null>(null);
    const [newAliasName, setNewAliasName] = useState("");
    const [isSavingAlias, setIsSavingAlias] = useState(false);

    // Cameo States (Sora app @username)
    const [savedCameos, setSavedCameos] = useState<{ id: string; username: string }[]>([]);
    const [cameoInstructionsOpen, setCameoInstructionsOpen] = useState(false);
    const [addCameoDialogOpen, setAddCameoDialogOpen] = useState(false);
    const [newCameoUsername, setNewCameoUsername] = useState("");

    const [recentLogs, setRecentLogs] = useState<any[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
    const [selectedErrorLog, setSelectedErrorLog] = useState<any | null>(null);

    // Gallery state
    const [showGallery, setShowGallery] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState<{ id: string; url: string; prompt: string }[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(false);

    // Continue without waiting states
    const [allowNewGeneration, setAllowNewGeneration] = useState(false);
    const [showContinueButton, setShowContinueButton] = useState(false);

    const seedanceModels = {
        "text-to-video": { id: "seedance-2-fast-text-to-video", name: "Seedance 2.0 Fast Text To Video", credits: { "10": 330, "15": 495 } },
        "image-to-video": { id: "seedance-2-fast-image-to-video", name: "Seedance 2.0 Fast Image To Video", credits: { "10": 330, "15": 495 } },
    };

    useEffect(() => {
        // Keep legacy /generate/sora route alive, but serve Seedance 2.0.
        const validTabs = ["text-to-video", "image-to-video"];
        if (!type || type === "text-to-video-stable" || type === "image-to-video-stable" || !validTabs.includes(type)) {
            navigate("/generate/sora/text-to-video", { replace: true });
            return;
        }
        if (type !== activeTab) {
            setActiveTab(type);
        }
    }, [type, navigate]);

    // Check for prompt in state (e.g. from Reuse button in History page)
    useEffect(() => {
        if (location.state?.prompt) {
            if (activeTab === "characters") {
                setCharacterPrompt(location.state.prompt);
            } else {
                setPromptText(location.state.prompt);
            }
        }
    }, [location.state, activeTab]);

    const handleTabChange = (val: string) => {
        setActiveTab(val);
        navigate(`/generate/sora/${val}`, { replace: true });
    };

    const fetchRecentLogs = async () => {
        if (!user) return;
        setLoadingLogs(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("*")
                .eq("user_id", user.id)
                .ilike("model", "%seedance%")
                .order("created_at", { ascending: false })
                .limit(10);

            if (error) throw error;
            setRecentLogs(data || []);
        } catch (err) {
            console.error("Error fetching logs:", err);
        } finally {
            setLoadingLogs(false);
        }
    };

    const fetchSavedCharacters = async () => {
        if (!user) return;
        try {
            const chars = await characterService.getMyCharacters();
            setSavedCharacters(chars);
        } catch (e) {
            console.error("Error fetching saved characters:", e);
        }
    };

    useEffect(() => {
        if (user) {
            fetchRecentLogs();
        }
    }, [user, activeTab]);

    // Cameo functions (localStorage-based)
    const loadSavedCameos = () => {
        if (!user) return;
        try {
            const stored = localStorage.getItem(`sora_cameos_${user.id}`);
            if (stored) {
                setSavedCameos(JSON.parse(stored));
            }
        } catch (e) {
            console.error("Error loading cameos:", e);
        }
    };

    const saveCameo = () => {
        if (!user || !newCameoUsername.trim()) return;

        let username = newCameoUsername.trim();
        // Ensure it starts with @ (but don't convert @@)
        if (!username.startsWith("@")) {
            username = "@" + username;
        }

        const newCameo = {
            id: `cameo_${Date.now()}`,
            username
        };

        const updated = [...savedCameos, newCameo];
        setSavedCameos(updated);
        localStorage.setItem(`sora_cameos_${user.id}`, JSON.stringify(updated));

        setNewCameoUsername("");
        setAddCameoDialogOpen(false);
        toast({ title: t("บันทึกสำเร็จ", "Saved successfully"), description: username });
    };

    const deleteCameo = (id: string) => {
        if (!user) return;
        const updated = savedCameos.filter(c => c.id !== id);
        setSavedCameos(updated);
        localStorage.setItem(`sora_cameos_${user.id}`, JSON.stringify(updated));
    };

    const handleSaveAlias = async () => {
        if (!newAliasName.trim() || !aliasToSave) return;

        let alias = newAliasName.trim();
        if (!alias.startsWith("@@")) {
            alias = "@@" + alias.replace(/^@+/, "");
        }

        setIsSavingAlias(true);
        try {
            await characterService.saveCharacter({
                alias,
                character_id: aliasToSave.characterId,
            });
            toast({ title: t("บันทึกสำเร็จ", "Saved successfully"), description: `${alias} is ready to use.` });
            setAliasDialogOpen(false);
            setNewAliasName("");
            fetchSavedCharacters();
        } catch (e: any) {
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: e.message || "Could not save alias."
            });
        } finally {
            setIsSavingAlias(false);
        }
    };

    const getRequiredCredits = () => {
        if (activeTab === "text-to-video" || activeTab === "image-to-video") {
            return nFrames === "15" ? 495 : 330;
        }
        return 0;
    };

    const fetchGalleryPhotos = async () => {
        if (!user) return;
        setLoadingGallery(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("id, prompt, result_url")
                .eq("user_id", user.id)
                .eq("service_type", "photo")
                .eq("status", "completed")
                .not("result_url", "is", null)
                .order("created_at", { ascending: false })
                .limit(24);

            if (error) throw error;

            const photos = (data || []).map(item => {
                let url = item.result_url || "";
                if (url.startsWith("{")) {
                    try {
                        const parsed = JSON.parse(url);
                        url = parsed.resultUrls?.[0] || parsed.url || url;
                    } catch { }
                }
                return {
                    id: item.id,
                    url: url,
                    prompt: item.prompt || ""
                };
            }).filter(p => p.url.startsWith("http"));

            setGalleryPhotos(photos);
        } catch (err) {
            console.error("Error fetching gallery photos:", err);
        } finally {
            setLoadingGallery(false);
        }
    };

    const handleSelectFromGallery = (url: string) => {
        setReferenceImageUrl(url);
        setReferenceImage(null);
        setShowGallery(false);
    };

    const handleGenerate = async () => {
        // Block if generating and not allowed to start new
        if (isGenerating && !allowNewGeneration) return;

        if (activeTab === "text-to-video" && !promptText.trim()) return;
        if (activeTab === "image-to-video" && (!promptText.trim() || (!referenceImage && !referenceImageUrl && !firstFrame))) {
            if (!promptText.trim()) toast({ variant: "destructive", title: t("กรุณากรอกพรอมต์", "Please enter a prompt") });
            else toast({ variant: "destructive", title: t("กรุณาอัปโหลดรูปภาพ", "Please upload an image") });
            return;
        }
        if (activeTab === "characters" && (!characterPrompt.trim() || (!characterVideo && !characterVideoUrl))) {
            if (!characterPrompt.trim()) toast({ variant: "destructive", title: t("กรุณากรอกลักษณะตัวละคร", "Please enter character traits") });
            else toast({ variant: "destructive", title: t("กรุณาอัปโหลดคลิปวิดีโอตัวอย่าง", "Please upload a character video clip") });
            return;
        }

        // Reset states when starting new generation
        setAllowNewGeneration(false);
        setShowContinueButton(true);

        const credits = getRequiredCredits();
        const model = seedanceModels[activeTab as keyof typeof seedanceModels]?.id || "seedance-2-fast-text-to-video";

        let options: any = {
            ratio: selectedRatio,
            n_frames: nFrames,
            duration: Number(nFrames),
            resolution: "720p",
            generate_audio: generateAudio,
            nsfw_checker: true,
            firstFrame,
            lastFrame,
            referenceImages,
            referenceVideos,
            referenceAudios,
            auto_retry: autoRetry,
            max_retries: 20,
        };

        if (activeTab === "image-to-video") {
            options.referenceImage = referenceImage || referenceImageUrl;
        }

        if (activeTab === "characters") {
            options.character_prompt = characterPrompt;
            options.safety_instruction = safetyInstruction;
            if (characterVideo || characterVideoUrl) {
                options.referenceVideo = characterVideo || characterVideoUrl;
            }
        }

        const result = await generate(
            "video",
            model,
            activeTab === "characters" ? characterPrompt : promptText,
            credits,
            options
        );

        // Hide continue button when done
        setShowContinueButton(false);

        if (result.success) {
            // Refresh history to show new job
            fetchRecentLogs();

            // Scroll to history section after a short delay to let the UI update
            setTimeout(() => {
                const historySection = document.getElementById('recent-history-section');
                if (historySection) {
                    historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);

            toast({
                title: t("กำลังสร้าง...", "Generating..."),
                description: t("คุณสามารถดูสถานะได้ที่ประวัติด้านล่าง", "Check status in history section below"),
            });
        }
    };

    const handleContinueWithoutWaiting = () => {
        setShowContinueButton(false);
        setAllowNewGeneration(true);
        toast({
            title: t("สร้างต่อได้เลย!", "You can continue!"),
            description: t("ผลลัพธ์ก่อนหน้าจะเก็บไว้ที่หน้า Dashboard", "Previous result will be saved to Dashboard"),
        });
    };

    const goToWatermark = (videoUrl: string) => {
        if (videoUrl) {
            navigate("/watermark", { state: { videoUrl } });
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setReferenceImage(e.target.files[0]);
            setReferenceImageUrl(null);
        }
    };

    const validateFiles = (
        files: File[],
        kind: "image" | "video" | "audio",
        maxFiles: number,
        maxSizeMb: number,
        acceptedTypes: string[]
    ) => {
        const valid = files.slice(0, maxFiles).filter(file => {
            const typeOk = acceptedTypes.includes(file.type);
            const sizeOk = file.size <= maxSizeMb * 1024 * 1024;
            if (!typeOk || !sizeOk) {
                toast({
                    variant: "destructive",
                    title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
                    description: `${file.name} (${kind}, max ${maxSizeMb}MB)`
                });
            }
            return typeOk && sizeOk;
        });
        return valid;
    };

    const handleFirstFrameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = validateFiles(Array.from(e.target.files || []), "image", 1, 30, ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif", "image/bmp"]);
        setFirstFrame(files[0] || null);
    };

    const handleLastFrameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = validateFiles(Array.from(e.target.files || []), "image", 1, 30, ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif", "image/bmp"]);
        setLastFrame(files[0] || null);
    };

    const handleReferenceImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReferenceImages(validateFiles(Array.from(e.target.files || []), "image", 5, 30, ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif", "image/bmp"]));
    };

    const handleReferenceVideosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReferenceVideos(validateFiles(Array.from(e.target.files || []), "video", 3, 50, ["video/mp4", "video/quicktime", "video/x-matroska"]));
    };

    const handleReferenceAudiosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReferenceAudios(validateFiles(Array.from(e.target.files || []), "audio", 3, 15, ["audio/mpeg", "audio/wav"]));
    };

    const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setCharacterVideo(e.target.files[0]);
            setCharacterVideoUrl(null);
        }
    };

    const renderStatusBadge = (status: string, log?: any) => {
        const maxRetries = log?.max_retries || 20;
        const retryCount = log?.retry_count || 0;

        // Auto retry badge
        const getRetryBadge = () => {
            if (!log?.auto_retry) return null;

            if (status === "completed" && retryCount > 0) {
                return (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-[10px]">
                        {t(`หลัง ${retryCount} ครั้ง`, `After ${retryCount}`)}
                    </Badge>
                );
            }

            if (status === "failed" && retryCount < maxRetries) {
                return (
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 animate-pulse text-[10px]">
                        {t(`รอซ้ำ ${retryCount + 1}/${maxRetries}`, `Retry ${retryCount + 1}/${maxRetries}`)}
                    </Badge>
                );
            }

            if (status === "failed" && retryCount >= maxRetries) {
                return (
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px]">
                        {t(`หมด ${maxRetries} ครั้ง`, `${maxRetries}x exhausted`)}
                    </Badge>
                );
            }

            return (
                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px]">
                    {t("เจนซ้ำ", "Auto")}
                </Badge>
            );
        };

        const retryBadge = getRetryBadge();

        switch (status) {
            case "completed":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-green-500">{t("สำเร็จ", "Completed")}</Badge>
                        {retryBadge}
                    </div>
                );
            case "processing":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-blue-500 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>
                        {retryBadge}
                    </div>
                );
            case "failed":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="destructive">{t("ล้มเหลว", "Failed")}</Badge>
                        {retryBadge}
                    </div>
                );
            default:
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline">{t("รอดำเนินการ", "Pending")}</Badge>
                        {retryBadge}
                    </div>
                );
        }
    };

    const handleCheckStatus = async (logId: string) => {
        setCheckingStatus(logId);
        try {
            const result = await checkTaskStatus(logId);
            if (result.success && result.status === "completed") {
                fetchRecentLogs();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setCheckingStatus(null);
        }
    };

    const formatDateHistory = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('th-TH', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const handleDeleteHistory = async (id: string) => {
        try {
            if (!confirm(t("คุณแน่ใจหรือไม่ที่จะลบรายการนี้?", "Are you sure you want to delete this item?"))) {
                return;
            }

            const { data: success, error } = await supabase.rpc('delete_generation_log' as any, { p_log_id: id });

            if (error) {
                const { error: dbError } = await supabase
                    .from("generation_logs")
                    .delete()
                    .eq("id", id);
                if (dbError) throw dbError;
            }

            toast({
                title: t("ลบสำเร็จ", "Deleted successfully"),
                description: t("รายการถูกลบออกจากประวัติแล้ว", "The item has been removed from history"),
            });

            fetchRecentLogs();
        } catch (error: any) {
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const handleReusePrompt = (prompt: string, model: string) => {
        // Determine target tab based on model
        let targetTab = "text-to-video";
        if (model.includes("image-to-video")) {
            targetTab = "image-to-video";
        } else if (model.includes("characters")) {
            targetTab = "characters";
        }

        // Set prompt based on target tab
        if (targetTab === "characters") {
            setCharacterPrompt(prompt);
        } else {
            setPromptText(prompt);
        }

        // Navigate to the correct tab if different from current
        if (activeTab !== targetTab) {
            handleTabChange(targetTab);
        }

        window.scrollTo(0, 0);
    };

    const handleDownload = async (url: string, filename: string) => {
        // Detect iOS devices
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        if (isIOS) {
            // iOS Safari doesn't support download attribute properly
            // Open in new tab so user can long-press to save video
            window.open(url, '_blank');
            toast({
                title: t("กดค้างที่วิดีโอเพื่อบันทึก", "Long-press on video to save"),
                description: t("iOS ไม่รองรับการดาวน์โหลดโดยตรง", "iOS doesn't support direct download"),
            });
            return;
        }

        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    const renderSeedanceReferences = () => (
        <Card className="border-border/50 bg-muted/20">
            <CardHeader className="pb-3">
                <CardTitle className="text-base">{t("ไฟล์ประกอบ", "Reference Media")}</CardTitle>
                <CardDescription>
                    {t(
                        "แนบภาพเริ่มต้น ภาพท้าย รูปภาพ วิดีโอ หรือเสียงประกอบสำหรับ Seedance 2.0 Fast",
                        "Attach first/last frames, reference images, videos, or audio for Seedance 2.0 Fast"
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">first_frame_url</label>
                        <label className="border-2 border-dashed border-border rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 bg-background/60 min-h-32">
                            {firstFrame ? (
                                <img src={URL.createObjectURL(firstFrame)} alt="First frame" className="max-h-24 rounded-lg object-contain" />
                            ) : (
                                <>
                                    <Upload className="h-6 w-6 text-primary mb-2" />
                                    <span className="text-sm text-center">{t("คลิกเพื่ออัปโหลดภาพเริ่มต้น", "Click to upload first frame")}</span>
                                    <span className="text-xs text-muted-foreground text-center">JPG/PNG/WEBP/BMP/GIF, max 30MB</span>
                                </>
                            )}
                            <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/jpg,image/gif,image/bmp" onChange={handleFirstFrameChange} />
                        </label>
                        {firstFrame && (
                            <Button variant="ghost" size="sm" onClick={() => setFirstFrame(null)} className="h-7 px-2 text-destructive">
                                <X className="h-3 w-3 mr-1" />{t("ลบ", "Remove")}
                            </Button>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">last_frame_url</label>
                        <label className="border-2 border-dashed border-border rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 bg-background/60 min-h-32">
                            {lastFrame ? (
                                <img src={URL.createObjectURL(lastFrame)} alt="Last frame" className="max-h-24 rounded-lg object-contain" />
                            ) : (
                                <>
                                    <Upload className="h-6 w-6 text-primary mb-2" />
                                    <span className="text-sm text-center">{t("คลิกเพื่ออัปโหลดภาพท้าย", "Click to upload last frame")}</span>
                                    <span className="text-xs text-muted-foreground text-center">JPG/PNG/WEBP/BMP/GIF, max 30MB</span>
                                </>
                            )}
                            <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/jpg,image/gif,image/bmp" onChange={handleLastFrameChange} />
                        </label>
                        {lastFrame && (
                            <Button variant="ghost" size="sm" onClick={() => setLastFrame(null)} className="h-7 px-2 text-destructive">
                                <X className="h-3 w-3 mr-1" />{t("ลบ", "Remove")}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">reference_image_urls</label>
                        <label className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 bg-background/60 min-h-28">
                            <ImageIcon className="h-5 w-5 text-primary mb-2" />
                            <span className="text-sm text-center">{t("เลือกรูปภาพประกอบ", "Select reference images")}</span>
                            <span className="text-xs text-muted-foreground text-center">Max 5 files, 30MB each</span>
                            <input type="file" multiple className="hidden" accept="image/jpeg,image/png,image/webp,image/jpg,image/gif,image/bmp" onChange={handleReferenceImagesChange} />
                        </label>
                        {referenceImages.length > 0 && <p className="text-xs text-muted-foreground">{referenceImages.length} {t("ไฟล์", "files")}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">reference_video_urls</label>
                        <label className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 bg-background/60 min-h-28">
                            <Video className="h-5 w-5 text-primary mb-2" />
                            <span className="text-sm text-center">{t("เลือกวิดีโอประกอบ", "Select reference videos")}</span>
                            <span className="text-xs text-muted-foreground text-center">MP4/QUICKTIME/X-MATROSKA, max 3 files, 50MB each</span>
                            <input type="file" multiple className="hidden" accept="video/mp4,video/quicktime,video/x-matroska" onChange={handleReferenceVideosChange} />
                        </label>
                        {referenceVideos.length > 0 && <p className="text-xs text-muted-foreground">{referenceVideos.length} {t("ไฟล์", "files")}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">reference_audio_urls</label>
                        <label className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 bg-background/60 min-h-28">
                            <Play className="h-5 w-5 text-primary mb-2" />
                            <span className="text-sm text-center">{t("เลือกเสียงประกอบ", "Select reference audio")}</span>
                            <span className="text-xs text-muted-foreground text-center">MPEG/WAV, max 3 files, 15MB each</span>
                            <input type="file" multiple className="hidden" accept="audio/mpeg,audio/wav" onChange={handleReferenceAudiosChange} />
                        </label>
                        {referenceAudios.length > 0 && <p className="text-xs text-muted-foreground">{referenceAudios.length} {t("ไฟล์", "files")}</p>}
                    </div>
                </div>

                <div className="flex items-center space-x-2 rounded-lg border border-border/50 bg-background/50 p-3">
                    <Checkbox
                        id="seedance-generate-audio"
                        checked={generateAudio}
                        onCheckedChange={(checked) => setGenerateAudio(checked === true)}
                    />
                    <label htmlFor="seedance-generate-audio" className="text-sm font-medium cursor-pointer">
                        generate_audio - {t("สร้างเสียง AI ที่ซิงก์กับวิดีโอ", "Generate AI audio synchronized with the video")}
                    </label>
                </div>
            </CardContent>
        </Card>
    );

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Navbar />
            {!user && <GuestOverlay />}

            <main className="pt-24 pb-16 container mx-auto px-4 max-w-6xl">
                <div className="flex flex-col items-center mb-10 text-center">
                    <Badge variant="outline" className="mb-4 py-1 px-4 text-primary border-primary/20 bg-primary/5">
                        <Sparkles className="h-3.5 w-3.5 mr-2" />
                        Seedance 2.0 Professional
                    </Badge>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 tracking-tight">
                        Seedance 2.0 <span className="text-gradient">Video Generation</span>
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-lg">
                        {t("สร้างวิดีโอคุณภาพสูงด้วย Seedance 2.0 จากข้อความหรือรูปภาพ พร้อมเสียง AI ที่ซิงก์กับวิดีโอ", "Create high-quality videos with Seedance 2.0 from text or images, with AI audio synchronized to the video.")}
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
                        <div className="hidden sm:block flex-1" />
                        <TabsList className="grid w-full max-w-xl grid-cols-2 h-12 p-1 bg-muted/50 backdrop-blur-sm border border-border/50">
                            <TabsTrigger value="text-to-video" className="data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all duration-300">
                                <Video className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Text To Video</span>
                            </TabsTrigger>
                            <TabsTrigger value="image-to-video" className="data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all duration-300">
                                <ImageIcon className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Image To Video</span>
                            </TabsTrigger>
                        </TabsList>
                        <div className="sm:flex-1 flex justify-end w-full sm:w-auto">
                            <Button variant="outline" size="sm" className="gap-2 text-primary border-primary/20 hover:bg-primary/10 rounded-full w-full sm:w-auto" asChild>
                                <Link to="/generate/videobulk">
                                    <Plus className="h-4 w-4" />
                                    {t("สร้างวิดีโอจำนวนมาก (Bulk)", "Bulk Video Generation")}
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-8 space-y-6">
                            <TabsContent value="text-to-video">
                                <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Video className="h-5 w-5 text-primary" />
                                            {t("สร้างวิดีโอจากข้อความ", "Text to Video")}
                                        </CardTitle>
                                        <CardDescription>{t("ระบุรายละเอียดวิดีโอที่คุณต้องการสร้าง", "Describe the video you want to create")}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <label className="text-sm font-bold flex items-center gap-2">
                                                    {t("คำบรรยาย (Prompt)", "Describe your video")}
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    {(savedCharacters.length > 0 || savedCameos.length > 0) && (
                                                        <span className="text-[10px] text-muted-foreground font-medium italic opacity-60">
                                                            {t("คลิกตัวละคร/Cameo ด้านล่างเพื่อใส่ชื่อ", "Click characters/cameos below to insert")}
                                                        </span>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="gap-1 text-muted-foreground hover:text-destructive h-7 px-2"
                                                        onClick={() => setPromptText("")}
                                                        disabled={!promptText}
                                                    >
                                                        <Eraser className="h-3.5 w-3.5" />
                                                        {t("ล้าง", "Clear")}
                                                    </Button>
                                                </div>
                                            </div>
                                            <Textarea
                                                placeholder={t("ตัวอย่าง: แพนด้ากำลังนั่งกินไผ่อย่างมีความสุขในป่าฝน...", "Example: A giant panda happily eating bamboo in a lush rainforest...")}
                                                value={promptText}
                                                onChange={(e) => setPromptText(e.target.value)}
                                                className="min-h-[150px] resize-none focus-visible:ring-primary shadow-inner text-lg p-4"
                                            />

                                            {/* Quick Character & Cameo Selection */}
                                            {(savedCharacters.length > 0 || savedCameos.length > 0) && (
                                                <div className="flex flex-wrap gap-2 pt-1">
                                                    {savedCharacters.map((char) => (
                                                        <Badge
                                                            key={char.id}
                                                            variant="outline"
                                                            className="cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-all py-1 px-3 flex items-center gap-2 bg-background/50 backdrop-blur-sm whitespace-nowrap group animate-in zoom-in-95"
                                                            onClick={() => {
                                                                setPromptText(prev => prev + (prev.length > 0 ? " " : "") + char.alias);
                                                                toast({ title: t("เพิ่มตัวละครแล้ว", "Character Added"), description: char.alias });
                                                            }}
                                                        >
                                                            <User className="h-3 w-3 text-primary" />
                                                            <span className="font-mono text-xs">{char.alias}</span>
                                                        </Badge>
                                                    ))}
                                                    {savedCameos.map((cameo) => (
                                                        <Badge
                                                            key={cameo.id}
                                                            variant="outline"
                                                            className="cursor-pointer hover:bg-pink-500/10 hover:border-pink-500/50 transition-all py-1 px-3 flex items-center gap-2 bg-background/50 backdrop-blur-sm whitespace-nowrap group animate-in zoom-in-95 border-pink-500/20"
                                                            onClick={() => {
                                                                setPromptText(prev => prev + (prev.length > 0 ? " " : "") + cameo.username);
                                                                toast({ title: t("เพิ่ม Cameo แล้ว", "Cameo Added"), description: cameo.username });
                                                            }}
                                                        >
                                                            <AtSign className="h-3 w-3 text-pink-500" />
                                                            <span className="font-mono text-xs text-pink-500">{cameo.username}</span>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {renderSeedanceReferences()}

                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">{t("สัดส่วน (Aspect Ratio)", "Aspect Ratio")}</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Button
                                                        variant={selectedRatio === "16:9" ? "default" : "outline"}
                                                        onClick={() => setSelectedRatio("16:9")}
                                                        className="w-full"
                                                    >
                                                        {t("แนวนอน (16:9)", "Landscape (16:9)")}
                                                    </Button>
                                                    <Button
                                                        variant={selectedRatio === "9:16" ? "default" : "outline"}
                                                        onClick={() => setSelectedRatio("9:16")}
                                                        className="w-full"
                                                    >
                                                        {t("แนวตั้ง (9:16)", "Portrait (9:16)")}
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">{t("ความยาว (Duration)", "Duration")}</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Button
                                                        variant={nFrames === "10" ? "default" : "outline"}
                                                        onClick={() => setNFrames("10")}
                                                        className="w-full"
                                                    >
                                                        {t("10 วิ", "10 s")}
                                                    </Button>
                                                    <Button
                                                        variant={nFrames === "15" ? "default" : "outline"}
                                                        onClick={() => setNFrames("15")}
                                                        className="w-full"
                                                    >
                                                        {t("15 วิ", "15 s")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Auto Retry Checkbox */}
                                        <div className="flex items-center space-x-2 py-2">
                                            <Checkbox
                                                id="auto-retry-text"
                                                checked={autoRetry}
                                                onCheckedChange={(checked) => setAutoRetry(checked === true)}
                                            />
                                            <label
                                                htmlFor="auto-retry-text"
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                                            >
                                                <RotateCcw className="h-4 w-4 text-amber-500" />
                                                {t("เจนซ้ำอัตโนมัติ (ถ้า 500 Error)", "Auto Retry (on 500 Error)")}
                                            </label>
                                        </div>

                                        <div className="space-y-2">
                                            <Button
                                                size="lg"
                                                className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
                                                onClick={handleGenerate}
                                                disabled={(isGenerating && !allowNewGeneration) || !promptText.trim()}
                                            >
                                                {isGenerating && !allowNewGeneration ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
                                                {t("สร้างวิดีโอ", "Generate Video")}
                                                <span className="ml-2 opacity-70 text-sm font-normal">({getRequiredCredits()} {t("เครดิต", "Credits")})</span>
                                            </Button>

                                            {/* Continue without waiting button */}
                                            {isGenerating && showContinueButton && !allowNewGeneration && (
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                    onClick={handleContinueWithoutWaiting}
                                                >
                                                    <FastForward className="h-5 w-5" />
                                                    {t("สร้างต่อไม่รอแล้ว", "Continue, don't wait")}
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="image-to-video">
                                <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <ImageIcon className="h-5 w-5 text-primary" />
                                            {t("สร้างวิดีโอจากรูปภาพ", "Image to Video")}
                                        </CardTitle>
                                        <CardDescription>{t("อัปโหลดรูปภาพเพื่อใช้เป็นเฟรมแรกของวิดีโอ", "Upload an image to use as the starting frame")}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium">{t("อัปโหลดรูปภาพ", "Upload Image")}</label>
                                                <div
                                                    className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/30"
                                                    onClick={() => document.getElementById('sora-image-upload')?.click()}
                                                >
                                                    {referenceImage ? (
                                                        <img
                                                            src={URL.createObjectURL(referenceImage)}
                                                            alt="Preview"
                                                            className="max-h-40 rounded-lg shadow-md"
                                                        />
                                                    ) : referenceImageUrl ? (
                                                        <img
                                                            src={referenceImageUrl}
                                                            alt="Preview"
                                                            className="max-h-40 rounded-lg shadow-md"
                                                        />
                                                    ) : (
                                                        <>
                                                            <div className="bg-primary/10 p-4 rounded-full mb-4">
                                                                <Upload className="h-8 w-8 text-primary" />
                                                            </div>
                                                            <p className="text-sm text-center text-muted-foreground">
                                                                {t("คลิกเพื่อเลือกไฟล์", "Click to choose file")} <br />
                                                                <span className="text-xs text-muted-foreground/60">(JPG, PNG, WEBP max 10MB)</span>
                                                            </p>
                                                        </>
                                                    )}
                                                    <input
                                                        id="sora-image-upload"
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={handleFileChange}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                    onClick={() => {
                                                        setShowGallery(true);
                                                        fetchGalleryPhotos();
                                                    }}
                                                >
                                                    <ImageIcon className="h-4 w-4" />
                                                    {t("เลือกจากภาพที่คุณเคยสร้าง", "Use from your Gallery")}
                                                </Button>
                                            </div>
                                            <div className="space-y-4">
                                                <div className="space-y-3">
                                                    <label className="text-sm font-medium">{t("การเคลื่อนไหว (Motion Prompt)", "Describe Motion")}</label>
                                                    <Textarea
                                                        placeholder={t("อธิบายว่าต้องการให้รูปภาพเคลื่อนไหวอย่างไร...", "Describe how you want the image to move...")}
                                                        value={promptText}
                                                        onChange={(e) => setPromptText(e.target.value)}
                                                        className="min-h-[120px] resize-none"
                                                    />

                                                    {/* Quick Character & Cameo Selection */}
                                                    {(savedCharacters.length > 0 || savedCameos.length > 0) && (
                                                        <div className="flex flex-wrap gap-2">
                                                            {savedCharacters.map((char) => (
                                                                <Badge
                                                                    key={char.id}
                                                                    variant="outline"
                                                                    className="cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-all py-1 px-2.5 flex items-center gap-2 bg-background/50 backdrop-blur-sm text-[10px]"
                                                                    onClick={() => {
                                                                        setPromptText(prev => prev + (prev.length > 0 ? " " : "") + char.alias);
                                                                        toast({ title: t("เพิ่มตัวละครแล้ว", "Character Added"), description: char.alias });
                                                                    }}
                                                                >
                                                                    <User className="h-2.5 w-2.5 text-primary" />
                                                                    <span className="font-mono">{char.alias}</span>
                                                                </Badge>
                                                            ))}
                                                            {savedCameos.map((cameo) => (
                                                                <Badge
                                                                    key={cameo.id}
                                                                    variant="outline"
                                                                    className="cursor-pointer hover:bg-pink-500/10 hover:border-pink-500/50 transition-all py-1 px-2.5 flex items-center gap-2 bg-background/50 backdrop-blur-sm text-[10px] border-pink-500/20"
                                                                    onClick={() => {
                                                                        setPromptText(prev => prev + (prev.length > 0 ? " " : "") + cameo.username);
                                                                        toast({ title: t("เพิ่ม Cameo แล้ว", "Cameo Added"), description: cameo.username });
                                                                    }}
                                                                >
                                                                    <AtSign className="h-2.5 w-2.5 text-pink-500" />
                                                                    <span className="font-mono text-pink-500">{cameo.username}</span>
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-6">
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium">{t("สัดส่วน", "Aspect Ratio")}</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Button
                                                                variant={selectedRatio === "16:9" ? "default" : "outline"}
                                                                onClick={() => setSelectedRatio("16:9")}
                                                                className="h-10"
                                                            >
                                                                {t("แนวนอน", "Landscape")}
                                                            </Button>
                                                            <Button
                                                                variant={selectedRatio === "9:16" ? "default" : "outline"}
                                                                onClick={() => setSelectedRatio("9:16")}
                                                                className="h-10"
                                                            >
                                                                {t("แนวตั้ง", "Portrait")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium">{t("ความยาว (Duration)", "Duration")}</label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Button
                                                                variant={nFrames === "10" ? "default" : "outline"}
                                                                onClick={() => setNFrames("10")}
                                                                className="h-10"
                                                            >
                                                                {t("10 วิ", "10 s")}
                                                            </Button>
                                                            <Button
                                                                variant={nFrames === "15" ? "default" : "outline"}
                                                                onClick={() => setNFrames("15")}
                                                                className="h-10"
                                                            >
                                                                {t("15 วิ", "15 s")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {renderSeedanceReferences()}

                                        {/* Auto Retry Checkbox */}
                                        <div className="flex items-center space-x-2 py-2">
                                            <Checkbox
                                                id="auto-retry-image"
                                                checked={autoRetry}
                                                onCheckedChange={(checked) => setAutoRetry(checked === true)}
                                            />
                                            <label
                                                htmlFor="auto-retry-image"
                                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2 cursor-pointer"
                                            >
                                                <RotateCcw className="h-4 w-4 text-amber-500" />
                                                {t("เจนซ้ำอัตโนมัติ (ถ้า 500 Error)", "Auto Retry (on 500 Error)")}
                                            </label>
                                        </div>

                                        <div className="space-y-2">
                                            <Button
                                                size="lg"
                                                className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40"
                                                onClick={handleGenerate}
                                                disabled={(isGenerating && !allowNewGeneration) || !promptText.trim() || (!referenceImage && !referenceImageUrl)}
                                            >
                                                {isGenerating && !allowNewGeneration ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
                                                {t("สร้างวิดีโอจากภาพ", "Generate from Image")}
                                                <span className="ml-2 opacity-70 text-sm font-normal">({getRequiredCredits()} {t("เครดิต", "Credits")})</span>
                                            </Button>

                                            {/* Continue without waiting button */}
                                            {isGenerating && showContinueButton && !allowNewGeneration && (
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                    onClick={handleContinueWithoutWaiting}
                                                >
                                                    <FastForward className="h-5 w-5" />
                                                    {t("สร้างต่อไม่รอแล้ว", "Continue, don't wait")}
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="characters">
                                <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <User className="h-5 w-5 text-primary" />
                                            {t("สร้างหน้าตาตัวละคร (Characters)", "Character Generation")}
                                        </CardTitle>
                                        <CardDescription>{t("สร้างลักษณะตัวละครที่แน่นอนเพื่อใช้ในวิดีโอ Sora 2", "Define stable character traits for Sora 2 videos")}</CardDescription>
                                        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                            <p className="text-red-500 text-sm font-bold flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4" />
                                                {t("กฎสำคัญ:", "Important Rules:")}
                                            </p>
                                            <ul className="text-red-500 text-xs mt-1 ml-6 list-disc space-y-0.5">
                                                <li>{t("ห้ามใช้คนจริง (No Real People)", "No Real People Allowed")}</li>
                                                <li>{t("ใช้ได้เฉพาะ 3D หรือ การ์ตูน เท่านั้น", "Only 3D or Cartoon characters allowed")}</li>
                                                <li>{t("สัตว์ใช้ได้ปกติ", "Animals are allowed")}</li>
                                            </ul>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-4">
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium">{t("อัปโหลดวิดีโอตัวอย่าง (Video Clip)", "Upload Character Video")}</label>
                                                <div
                                                    className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/30"
                                                    onClick={() => document.getElementById('sora-character-video')?.click()}
                                                >
                                                    {characterVideo ? (
                                                        <div className="flex flex-col items-center">
                                                            <Video className="h-8 w-8 text-primary mb-2" />
                                                            <p className="text-xs text-center truncate max-w-[200px]">{characterVideo.name}</p>
                                                        </div>
                                                    ) : characterVideoUrl ? (
                                                        <video src={characterVideoUrl} className="max-h-32 rounded" />
                                                    ) : (
                                                        <>
                                                            <div className="bg-primary/10 p-4 rounded-full mb-3">
                                                                <Upload className="h-6 w-6 text-primary" />
                                                            </div>
                                                            <p className="text-xs text-center text-muted-foreground leading-relaxed">
                                                                {t("คลิกเพื่ออัปโหลดคลิปวิดีโอ 1-4 วินาที", "Click to upload 1-4s video clip")} <br />
                                                                <span className="opacity-60">(MP4, MOV, WEBM, M4V, AVI - max 100MB)</span>
                                                            </p>
                                                        </>
                                                    )}
                                                    <input
                                                        id="sora-character-video"
                                                        type="file"
                                                        className="hidden"
                                                        accept="video/mp4,video/mov,video/webm,video/x-m4v,video/avi,video/*"
                                                        onChange={handleVideoChange}
                                                    />
                                                </div>
                                                <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border/30 space-y-1">
                                                    <p className="font-medium text-foreground/80">{t("เงื่อนไขวิดีโอ:", "Video Requirements:")}</p>
                                                    <ul className="list-disc list-inside space-y-0.5 ml-1">
                                                        <li>{t("ความยาว 1-4 วินาที, ขนาดไม่เกิน 100 MB", "Duration 1-4 seconds, max 100 MB")}</li>
                                                        <li>{t("ใช้ตัวละครที่ไม่ใช่คนจริง (ห้ามใช้ภาพคนจริง)", "Use non-real person (no real people)")}</li>
                                                        <li>{t("วางตัวละครไว้กลางเฟรม แสงสว่างเพียงพอ", "Keep subject centered, well-lit")}</li>
                                                        <li>{t("หลีกเลี่ยงภาพเบลอจากการเคลื่อนไหว", "Avoid motion blur for best recognition")}</li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">{t("ลักษณะตัวละคร (Character Traits)", "Character Traits")}</label>
                                                <Textarea
                                                    placeholder={t("ตัวอย่าง: บาริสต้าร่าเริง ใส่ผ้ากันเปื้อนสีเขียว ยิ้มแย้ม...", "Example: Cheerful barista, green apron, warm smile...")}
                                                    value={characterPrompt}
                                                    onChange={(e) => setCharacterPrompt(e.target.value)}
                                                    className="min-h-[100px] resize-none"
                                                />
                                                <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border/30 space-y-1">
                                                    <p className="font-medium text-foreground/80">{t("คำแนะนำ:", "Tips:")}</p>
                                                    <ul className="list-disc list-inside space-y-0.5 ml-1">
                                                        <li>{t("เขียนสั้นๆ 1 บรรทัด ระบุลักษณะคงที่", "Write one short line with stable traits")}</li>
                                                        <li>{t("ตัวอย่าง: \"บาริสต้าร่าเริง ผ้ากันเปื้อนสีเขียว ยิ้มอบอุ่น\"", "Example: \"cheerful barista, green apron, warm smile\"")}</li>
                                                        <li>{t("หลีกเลี่ยงคำสั่งมุมกล้อง หรือคำขัดแย้งกัน", "Avoid camera directions or contradictions")}</li>
                                                        <li className="text-amber-500">{t("ห้ามใช้ชื่อหรือหน้าตาคนดัง", "No celebrity names or likeness")}</li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-amber-500/80">{t("ข้อกำหนดความปลอดภัย (Safety Instructions)", "Safety Boundaries")}</label>
                                                <Input
                                                    placeholder={t("เช่น: ไม่มีความรุนแรง, ไม่มีการเมือง, PG-13...", "E.g.: No violence, no politics, PG-13...")}
                                                    value={safetyInstruction}
                                                    onChange={(e) => setSafetyInstruction(e.target.value)}
                                                />
                                                <p className="text-xs text-muted-foreground">
                                                    {t("ระบุข้อจำกัดเนื้อหา เช่น \"ไม่มีความรุนแรง, การเมือง, แอลกอฮอล์; PG-13\" ยิ่งระบุชัดเจน AI จะปฏิบัติตามได้ดีขึ้น", "List boundaries like \"no violence, politics, alcohol; PG-13 max\" - tighter wording helps enforce content limits")}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Button
                                                size="lg"
                                                className="w-full h-14 text-lg font-bold border-2 border-primary/20 hover:border-primary/50 transition-all shadow-lg shadow-primary/10"
                                                onClick={handleGenerate}
                                                disabled={(isGenerating && !allowNewGeneration) || !characterPrompt.trim() || (!characterVideo && !characterVideoUrl)}
                                            >
                                                {isGenerating && !allowNewGeneration ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <User className="mr-2 h-5 w-5" />}
                                                {t("บันทึกโมเดลตัวละคร", "Generate Character ID")}
                                                <span className="ml-2 opacity-70 text-sm font-normal">(20 {t("เครดิต", "Credits")})</span>
                                            </Button>

                                            {/* Continue without waiting button */}
                                            {isGenerating && showContinueButton && !allowNewGeneration && (
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10"
                                                    onClick={handleContinueWithoutWaiting}
                                                >
                                                    <FastForward className="h-5 w-5" />
                                                    {t("สร้างต่อไม่รอแล้ว", "Continue, don't wait")}
                                                </Button>
                                            )}
                                        </div>

                                        <div className="bg-muted/30 p-4 rounded-lg border border-border/50">
                                            <h4 className="flex items-center text-sm font-semibold mb-2">
                                                <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                                                {t("Sora 2 Characters คืออะไร?", "What is Sora 2 Characters?")}
                                            </h4>
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                {t("ระบบจะสร้าง Character ID ขึ้นมาเพื่อให้คุณนำไปใช้ในพรอมต์ของ Sora 2 ในภายหลัง เพื่อให้ตัวละครหน้าตาคงเดิมตลอดวิดีโอทุกคลิป", "Generates a Character ID to use in your Sora 2 prompts, ensuring character consistency across all your videos.")}
                                            </p>
                                        </div>

                                        <div className="border-t border-border/50 pt-6 mt-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="text-sm font-bold flex items-center gap-2">
                                                    <Badge variant="outline" className="h-5 w-5 p-0 flex items-center justify-center rounded-full bg-primary/10 text-primary border-primary/20">{savedCharacters.length}</Badge>
                                                    {t("ตัวละครที่คุณบันทึกไว้ (Aliases)", "Your Saved Aliases")}
                                                </h4>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 text-[10px] font-bold uppercase tracking-wider gap-2 border-primary/20 hover:bg-primary/5 text-primary rounded-full"
                                                    onClick={() => {
                                                        setAliasToSave({ characterId: "", logId: "manual" });
                                                        setNewAliasName("");
                                                        setAliasDialogOpen(true);
                                                    }}
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    {t("เพิ่มเอง", "Add Manual")}
                                                </Button>
                                            </div>

                                            {savedCharacters.length === 0 ? (
                                                <div className="bg-muted/20 border border-dashed border-border/50 rounded-xl p-8 text-center">
                                                    <div className="bg-background/80 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                                                        <User className="h-6 w-6 text-muted-foreground/40" />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mb-1 font-medium">{t("ยังไม่มีตัวละครที่บันทึกไว้", "No saved characters yet")}</p>
                                                    <p className="text-[10px] text-muted-foreground/60 max-w-[200px] mx-auto">
                                                        {t("สร้างหน้าตาตัวละครใหม่ แล้วกดปุ่ม + ในประวัติเพื่อตั้งชื่อเรียก (Alias)", "Generate a character, then click the + button in history to set an alias.")}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {savedCharacters.map((char) => (
                                                        <div key={char.id} className="bg-background/40 border border-border/50 rounded-xl p-3 flex items-center justify-between group hover:border-primary/30 transition-all shadow-sm">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shadow-sm">
                                                                    <User className="h-4 w-4 text-primary" />
                                                                </div>
                                                                <div className="overflow-hidden">
                                                                    <p className="text-xs font-bold font-mono text-primary truncate">{char.alias}</p>
                                                                    <p className="text-[9px] text-muted-foreground font-mono truncate opacity-60">{char.character_id}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7 rounded-lg hover:bg-primary/10 hover:text-primary"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(char.character_id);
                                                                        toast({ title: t("คัดลอก ID แล้ว", "ID Copied") });
                                                                    }}
                                                                >
                                                                    <Copy className="h-3 w-3" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                                                                    onClick={() => {
                                                                        if (confirm(t("ต้องการลบตัวละครนี้หรือไม่?", "Delete this character?"))) {
                                                                            characterService.deleteCharacter(char.id).then(() => fetchSavedCharacters());
                                                                        }
                                                                    }}
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Cameo Section - Sora App @username */}
                                        <div className="border-t border-border/50 pt-6 mt-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-sm font-bold flex items-center gap-2">
                                                        <Badge variant="outline" className="h-5 w-5 p-0 flex items-center justify-center rounded-full bg-pink-500/10 text-pink-500 border-pink-500/20">{savedCameos.length}</Badge>
                                                        Cameo {t("ตัวละครของคุณ", "Characters")}
                                                    </h4>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 rounded-full text-muted-foreground hover:text-pink-500 hover:bg-pink-500/10"
                                                        onClick={() => setCameoInstructionsOpen(true)}
                                                    >
                                                        <HelpCircle className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 text-[10px] font-bold uppercase tracking-wider gap-2 border-pink-500/20 hover:bg-pink-500/5 text-pink-500 rounded-full"
                                                    onClick={() => {
                                                        setNewCameoUsername("");
                                                        setAddCameoDialogOpen(true);
                                                    }}
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    {t("เพิ่มเอง", "Add")}
                                                </Button>
                                            </div>

                                            <p className="text-[10px] text-muted-foreground mb-4 bg-muted/30 p-2 rounded-lg border border-border/30">
                                                <span className="text-pink-500">Cameo</span> {t("คือตัวละครที่สร้างจาก Sora App (ต้องเปิด public ก่อน) ใช้ @username ของคุณได้เลย", "is a character created in Sora App (must enable public). Use your @username directly.")}
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="h-auto p-0 ml-1 text-[10px] text-pink-500 underline"
                                                    onClick={() => setCameoInstructionsOpen(true)}
                                                >
                                                    {t("ดูวิธี", "How?")}
                                                </Button>
                                            </p>

                                            {savedCameos.length === 0 ? (
                                                <div className="bg-muted/20 border border-dashed border-pink-500/20 rounded-xl p-8 text-center">
                                                    <div className="bg-background/80 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                                                        <AtSign className="h-6 w-6 text-pink-500/40" />
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mb-1 font-medium">{t("ยังไม่มี Cameo ที่บันทึกไว้", "No saved Cameos yet")}</p>
                                                    <p className="text-[10px] text-muted-foreground/60 max-w-[220px] mx-auto">
                                                        {t("สร้าง Cameo ในแอพ Sora แล้วนำ @username มาใช้ที่นี่", "Create a Cameo in Sora app, then add your @username here.")}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {savedCameos.map((cameo) => (
                                                        <div key={cameo.id} className="bg-background/40 border border-pink-500/20 rounded-xl p-3 flex items-center justify-between group hover:border-pink-500/40 transition-all shadow-sm">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-pink-500/10 flex items-center justify-center shadow-sm">
                                                                    <AtSign className="h-4 w-4 text-pink-500" />
                                                                </div>
                                                                <div className="overflow-hidden">
                                                                    <p className="text-xs font-bold font-mono text-pink-500 truncate">{cameo.username}</p>
                                                                    <p className="text-[9px] text-muted-foreground truncate opacity-60">Sora Cameo</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7 rounded-lg hover:bg-pink-500/10 hover:text-pink-500"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(cameo.username);
                                                                        toast({ title: t("คัดลอกแล้ว", "Copied"), description: cameo.username });
                                                                    }}
                                                                >
                                                                    <Copy className="h-3 w-3" />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                                                                    onClick={() => {
                                                                        if (confirm(t("ต้องการลบ Cameo นี้หรือไม่?", "Delete this Cameo?"))) {
                                                                            deleteCameo(cameo.id);
                                                                        }
                                                                    }}
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                        </div>

                        <div className="lg:col-span-4 space-y-6">
                            {/* Current Task Status */}
                            {isGenerating && (
                                <Card className="border-primary/30 bg-primary/5 backdrop-blur-md shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-500">
                                    <div className="absolute top-0 right-0 w-32 h-32 -mr-12 -mt-12 bg-primary/10 rounded-full blur-3xl"></div>
                                    <div className="absolute bottom-0 left-0 w-24 h-24 -ml-8 -mb-8 bg-purple-500/10 rounded-full blur-2xl"></div>

                                    <CardContent className="pt-8 pb-8 flex flex-col items-center text-center relative z-10">
                                        <div className="relative mb-6">
                                            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                                            <div className="bg-background/80 backdrop-blur-sm rounded-full p-4 border border-primary/20 shadow-xl relative">
                                                <Loader2 className="h-10 w-10 text-primary animate-spin" />
                                            </div>
                                        </div>

                                        <h3 className="text-xl font-bold mb-2 text-foreground">
                                            {t("กำลังสร้างเนื้อหา...", "Generating content...")}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-8 max-w-[200px]">
                                            {t("โปรดรอสักครู่ ระบบกำลังประมวลผลคำขอของคุณ", "Please wait while we process your request")}
                                        </p>

                                        <div className="w-full space-y-3 px-2">
                                            <div className="flex justify-between items-end text-xs mb-1">
                                                <span className="font-bold text-primary/80 uppercase tracking-widest">{t("ความคืบหน้า", "Progress")}</span>
                                                <span className="font-mono font-bold text-primary">{Math.round(generationProgress)}% {t("เสร็จสิ้น", "complete")}</span>
                                            </div>
                                            <Progress value={generationProgress} className="h-2.5 bg-primary/10" />

                                            <div className="flex items-center justify-center gap-2 pt-2">
                                                <div className="h-2 w-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(var(--primary),0.8)]"></div>
                                                <span className="text-xs font-medium text-foreground/70">{statusMessage}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Credit Usage */}
                            <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-lg overflow-hidden relative group">
                                <div className="absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all duration-500"></div>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-md flex items-center justify-between">
                                        {t("เครดิตของคุณ", "Your Credits")}
                                        <Coins className="h-4 w-4 text-amber-500" />
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-baseline gap-2 mb-4">
                                        <span className="text-4xl font-bold text-gradient">{profile?.credits || 0}</span>
                                        <span className="text-muted-foreground text-sm uppercase tracking-wider font-medium">{t("เครดิตคงเหลือ", "Credits")}</span>
                                    </div>
                                    <Button
                                        variant="hero"
                                        size="sm"
                                        className="w-full rounded-full"
                                        onClick={() => navigate("/pricing")}
                                    >
                                        {t("เติมเครดิต", "Top Up Credits")}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </Tabs>

                {/* Recent History Section - Redesigned to match Generate.tsx */}
                <div className="mt-12" id="recent-history-section">
                    <Card className="rounded-3xl border border-border/50 shadow-xl bg-card/50 backdrop-blur-md p-6 md:p-8">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                                    <History className="h-6 w-6 text-primary" />
                                    {t("วิดีโอล่าสุด", "Recent History")}
                                </h2>
                                <p className="text-sm text-muted-foreground">{t("ผลลัพธ์ที่คุณสร้างล่าสุดด้วย Seedance 2.0", "Your recently generated Seedance 2.0 results")}</p>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="icon" onClick={fetchRecentLogs} disabled={loadingLogs} className="rounded-full h-10 w-10">
                                    <RefreshCw className={`h-5 w-5 ${loadingLogs ? "animate-spin" : ""}`} />
                                </Button>
                                <Button variant="outline" className="gap-2 rounded-full px-5" onClick={() => navigate("/history")}>
                                    <History className="h-4 w-4" />
                                    {t("ดูทั้งหมด", "View All")}
                                </Button>
                            </div>
                        </div>

                        {loadingLogs ? (
                            <div className="flex flex-col items-center justify-center py-20 grayscale opacity-50">
                                <Loader2 className="h-10 w-10 animate-spin mb-4" />
                                <p>{t("กำลังโหลดข้อมูล...", "Loading your history...")}</p>
                            </div>
                        ) : recentLogs.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {recentLogs.map((log) => {
                                    const isCompleted = log.status === "completed";
                                    const isFailed = log.status === "failed";
                                    const isProcessing = log.status === "processing";
                                    const resultUrl = log.result_url;

                                    return (
                                        <Card
                                            key={log.id}
                                            className="group overflow-hidden border border-border/40 shadow-lg bg-background/60 backdrop-blur-sm hover:shadow-2xl hover:border-primary/30 transition-all duration-500 rounded-3xl flex flex-col h-full"
                                        >
                                            {/* Media Section */}
                                            <div className="p-3">
                                                <div className={`relative rounded-2xl overflow-hidden bg-muted/50 border border-border/10 shadow-inner group/media ${(!isCompleted || log.model.includes('characters')) ? 'aspect-video' : ''}`}>
                                                    {isCompleted && resultUrl ? (
                                                        log.model.includes('characters') ? (
                                                            <div className="h-full flex flex-col items-center justify-center p-6 bg-gradient-to-br from-primary/5 to-purple-500/5">
                                                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 shadow-sm group-hover/media:scale-110 transition-transform duration-500">
                                                                    <User className="h-8 w-8 text-primary/60" />
                                                                </div>
                                                                <code className="text-[10px] font-mono bg-background/80 px-3 py-1.5 rounded-lg border border-border/50 truncate w-full text-center shadow-sm">
                                                                    {(() => {
                                                                        if (resultUrl?.startsWith('character_id:')) {
                                                                            return resultUrl.split(':')[1];
                                                                        }
                                                                        try {
                                                                            const parsed = JSON.parse(resultUrl || '{}');
                                                                            return parsed.resultObject?.character_id || resultUrl;
                                                                        } catch (e) {
                                                                            return resultUrl;
                                                                        }
                                                                    })()}
                                                                </code>
                                                            </div>
                                                        ) : (
                                                            <div className="relative group/video">
                                                                <video
                                                                    src={resultUrl}
                                                                    className="w-full h-auto max-h-[600px] block bg-black/10"
                                                                    controls
                                                                    playsInline
                                                                    preload="metadata"
                                                                />
                                                                {/* Overlay Action Buttons */}
                                                                <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover/video:opacity-100 transition-opacity duration-300">
                                                                    <Button
                                                                        size="icon"
                                                                        variant="secondary"
                                                                        className="h-8 w-8 rounded-lg bg-background/80 backdrop-blur-sm shadow-md hover:bg-background border border-border/50"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(resultUrl);
                                                                            toast({ title: t("คัดลอก URL แล้ว", "URL Copied") });
                                                                        }}
                                                                        title={t("คัดลอก URL", "Copy URL")}
                                                                    >
                                                                        <Copy className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="secondary"
                                                                        className="h-8 w-8 rounded-lg bg-background/80 backdrop-blur-sm shadow-md hover:bg-background border border-border/50"
                                                                        onClick={() => handleDownload(resultUrl, `sora2-gen-${log.id.slice(0, 8)}.mp4`)}
                                                                        title={t("ดาวน์โหลด", "Download")}
                                                                    >
                                                                        <Download className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="secondary"
                                                                        className="h-8 w-8 rounded-lg bg-amber-500/90 hover:bg-amber-500 text-white shadow-md border-0"
                                                                        onClick={() => goToWatermark(resultUrl)}
                                                                        title={t("ใส่ลายน้ำ", "Add Watermark")}
                                                                    >
                                                                        <Stamp className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                                                            {isFailed ? (
                                                                <button
                                                                    onClick={() => setSelectedErrorLog(log)}
                                                                    className="flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                                                                    title={t("คลิกเพื่อดูสาเหตุ", "Click to see reason")}
                                                                >
                                                                    <AlertTriangle className="h-10 w-10 text-destructive/50" />
                                                                    <p className="text-[10px] uppercase font-bold tracking-widest text-destructive/60">{t("ล้มเหลว", "Failed")}</p>
                                                                    <p className="text-[9px] text-destructive/40 underline">{t("ดูสาเหตุ", "See reason")}</p>
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <Loader2 className="h-10 w-10 text-primary/30 animate-spin" />
                                                                    <p className="text-[10px] uppercase font-bold tracking-widest text-primary/40 animate-pulse">{t("กำลังสร้าง...", "Generating")}</p>
                                                                    {isProcessing && (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => handleCheckStatus(log.id)}
                                                                            className="mt-2 h-7 rounded-full text-[10px] px-3 border-primary/20 hover:bg-primary/5"
                                                                            disabled={checkingStatus === log.id}
                                                                        >
                                                                            {checkingStatus === log.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                                                                            {t("เช็กสถานะ", "Check Status")}
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Details Section */}
                                            <CardContent className="p-5 pt-0 flex-1 flex flex-col">
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-primary/60 bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">
                                                        {log.model.split('-').slice(0, 3).join(' ')}
                                                    </span>
                                                    {renderStatusBadge(log.status, log)}
                                                </div>

                                                <p className="text-sm font-medium line-clamp-2 italic text-foreground/80 mb-6 flex-1">
                                                    "{log.prompt}"
                                                </p>

                                                <div className="flex items-center justify-between pt-4 border-t border-border/30">
                                                    <div className="flex gap-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter mb-0.5">{t("วันที่", "Date")}</span>
                                                            <span className="text-[11px] font-semibold text-foreground/70">{formatDateHistory(log.created_at)}</span>
                                                        </div>
                                                        {log.credits_used !== undefined && (
                                                            <div className="flex flex-col border-l border-border/30 pl-4">
                                                                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter mb-0.5">{t("เครดิต", "Credits")}</span>
                                                                {log.status === "failed" ? (
                                                                    <span className="text-[11px] font-bold text-green-500">0 ({t("คืนแล้ว", "Refunded")})</span>
                                                                ) : (
                                                                    <span className="text-[11px] font-bold text-primary">-{log.credits_used}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                                                            onClick={() => handleReusePrompt(log.prompt, log.model)}
                                                            title={t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                                        >
                                                            <Repeat className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-all"
                                                            onClick={() => handleDeleteHistory(log.id)}
                                                            title={t("ลบ", "Delete")}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                        {isCompleted && resultUrl && (
                                                            log.model.includes('characters') ? (
                                                                <div className="flex gap-1">
                                                                    <Button
                                                                        size="icon"
                                                                        variant="ghost"
                                                                        className="h-8 w-8 text-primary hover:bg-primary/5 rounded-xl transition-all"
                                                                        onClick={() => {
                                                                            const charId = resultUrl.startsWith('character_id:') ? resultUrl.split(':')[1] : resultUrl;
                                                                            setAliasToSave({ characterId: charId, logId: log.id });
                                                                            setNewAliasName("");
                                                                            setAliasDialogOpen(true);
                                                                        }}
                                                                        title={t("ตั้งชื่อเรียก (Alias)", "Set Alias")}
                                                                    >
                                                                        <Plus className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="ghost"
                                                                        className="h-8 w-8 text-primary hover:bg-primary/5 rounded-xl transition-all"
                                                                        onClick={() => {
                                                                            const id = resultUrl.startsWith('character_id:') ? resultUrl.split(':')[1] : resultUrl;
                                                                            navigator.clipboard.writeText(id);
                                                                            toast({ title: t("คัดลอก Character ID แล้ว", "Character ID Copied") });
                                                                        }}
                                                                        title={t("คัดลอก ID", "Copy ID")}
                                                                    >
                                                                        <Copy className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-1">
                                                                    <Button
                                                                        size="icon"
                                                                        variant="ghost"
                                                                        className="h-8 w-8 text-amber-500 hover:bg-amber-500/10 rounded-xl transition-all"
                                                                        onClick={() => goToWatermark(resultUrl)}
                                                                        title={t("ใส่ลายน้ำ", "Add Watermark")}
                                                                    >
                                                                        <Stamp className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="ghost"
                                                                        className="h-8 w-8 text-primary hover:bg-primary/5 rounded-xl transition-all"
                                                                        onClick={() => handleDownload(resultUrl, `sora2-gen-${log.id.slice(0, 8)}.mp4`)}
                                                                        title={t("ดาวน์โหลด", "Download")}
                                                                    >
                                                                        <Download className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground grayscale opacity-30">
                                <History className="h-16 w-16 mb-4" />
                                <p className="text-lg font-medium tracking-tight">{t("ยังไม่มีประวัติการสร้าง", "No history yet")}</p>
                            </div>
                        )}
                    </Card>
                </div>
            </main>

            {/* Gallery Modal */}
            {
                showGallery && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
                        onClick={() => setShowGallery(false)}
                    >
                        <div
                            className="bg-background rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-border flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-border flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold">{t("แกลเลอรีของคุณ", "Your Gallery")}</h3>
                                    <p className="text-sm text-muted-foreground">{t("เลือกจากรูปภาพที่คุณเคยสร้างไว้", "Choose from your previous generations")}</p>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => setShowGallery(false)}>
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                                {loadingGallery ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                                    </div>
                                ) : galleryPhotos.length === 0 ? (
                                    <div className="text-center py-20 opacity-50">
                                        <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                                        <p>{t("ไม่พบรูปภาพในแกลเลอรี", "No images found in gallery")}</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                        {galleryPhotos.map((photo) => (
                                            <div
                                                key={photo.id}
                                                className="group relative aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all shadow-md"
                                                onClick={() => handleSelectFromGallery(photo.url)}
                                            >
                                                <img
                                                    src={photo.url}
                                                    alt={photo.prompt}
                                                    className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                                                />
                                                <div className="absolute inset-x-0 bottom-0 p-2 bg-black/60 translate-y-full group-hover:translate-y-0 transition-transform">
                                                    <p className="text-[10px] text-white line-clamp-2 italic">
                                                        {photo.prompt}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="p-4 border-t border-border bg-muted/30 flex justify-end">
                                <Button variant="ghost" onClick={() => setShowGallery(false)}>
                                    {t("ยกเลิก", "Cancel")}
                                </Button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Alias Mapping Dialog */}
            <Dialog open={aliasDialogOpen} onOpenChange={setAliasDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t("ตั้งชื่อเรียกตัวละคร", "Set Character Alias")}</DialogTitle>
                        <DialogDescription>
                            {t("ใช้ชื่อย่อ (Alias) เพื่อเรียกใช้ตัวละครนี้ได้ง่ายในภายหลัง (เช่น @@aikrub)", "Set an alias to easily use this character in future prompts (e.g. @@aikrub)")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t("ชื่อเรียก (เช่น @@hero)", "Alias (e.g. @@hero)")}</label>
                            <Input
                                placeholder="@@..."
                                value={newAliasName}
                                onChange={(e) => setNewAliasName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveAlias()}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t("Character ID ที่ต้องการบันทึก:", "Character ID to save:")}</label>
                            {aliasToSave?.logId === "manual" ? (
                                <Input
                                    placeholder="chr_xxxxxxxxxxxxxxxx"
                                    value={aliasToSave?.characterId || ""}
                                    onChange={(e) => setAliasToSave(prev => prev ? { ...prev, characterId: e.target.value } : null)}
                                />
                            ) : (
                                <div className="bg-muted/30 p-3 rounded-lg border border-border/50">
                                    <code className="text-[10px] break-all opacity-70">{aliasToSave?.characterId}</code>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAliasDialogOpen(false)}>{t("ยกเลิก", "Cancel")}</Button>
                        <Button onClick={handleSaveAlias} disabled={isSavingAlias || !newAliasName.trim()}>
                            {isSavingAlias ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                            {t("บันทึก", "Save Alias")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cameo Instructions Dialog */}
            <Dialog open={cameoInstructionsOpen} onOpenChange={setCameoInstructionsOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AtSign className="h-5 w-5 text-pink-500" />
                            {t("วิธีสร้าง Cameo ในแอพ Sora", "How to Create Cameo in Sora App")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("ทำตามขั้นตอนเหล่านี้เพื่อสร้าง Cameo ของคุณ", "Follow these steps to create your Cameo")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-3">
                            <div className="flex gap-3 items-start bg-muted/30 p-3 rounded-lg border border-border/30">
                                <div className="w-7 h-7 rounded-full bg-pink-500/20 flex items-center justify-center shrink-0 font-bold text-pink-500 text-sm">1</div>
                                <div>
                                    <p className="text-sm font-medium">{t("เปิดแอพ Sora บน iOS", "Open Sora app on iOS")}</p>
                                    <p className="text-xs text-muted-foreground">{t("ดาวน์โหลดแอพ Sora จาก App Store (เฉพาะ iOS)", "Download Sora from App Store (iOS only)")}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start bg-muted/30 p-3 rounded-lg border border-border/30">
                                <div className="w-7 h-7 rounded-full bg-pink-500/20 flex items-center justify-center shrink-0 font-bold text-pink-500 text-sm">2</div>
                                <div>
                                    <p className="text-sm font-medium">{t("สร้าง Cameo ใหม่", "Create new Cameo")}</p>
                                    <p className="text-xs text-muted-foreground">{t("ไปที่ Profile > Create Cameo แล้วแสกนใบหน้าของคุณ", "Go to Profile > Create Cameo and scan your face")}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start bg-muted/30 p-3 rounded-lg border border-border/30">
                                <div className="w-7 h-7 rounded-full bg-pink-500/20 flex items-center justify-center shrink-0 font-bold text-pink-500 text-sm">3</div>
                                <div>
                                    <p className="text-sm font-medium text-amber-500">{t("เปิด Public (สำคัญ!)", "Enable Public (Important!)")}</p>
                                    <p className="text-xs text-muted-foreground">{t("ในหน้า Cameo Settings ให้เปิด \"Allow my cameo to be used by others\" เพื่อให้ใช้งานได้จากภายนอก", "In Cameo Settings, enable \"Allow my cameo to be used by others\" to use from external apps")}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start bg-muted/30 p-3 rounded-lg border border-border/30">
                                <div className="w-7 h-7 rounded-full bg-pink-500/20 flex items-center justify-center shrink-0 font-bold text-pink-500 text-sm">4</div>
                                <div>
                                    <p className="text-sm font-medium">{t("นำ @username มาใช้ที่นี่", "Use your @username here")}</p>
                                    <p className="text-xs text-muted-foreground">{t("คัดลอก @username ของคุณจากแอพ Sora แล้วมาเพิ่มที่นี่ได้เลย", "Copy your @username from Sora app and add it here")}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-600 dark:text-amber-400">
                            <strong>{t("หมายเหตุ:", "Note:")}</strong> {t("Cameo จะใช้ @username ตรงๆ จากแอพ Sora ไม่ต้องแปลงหรือแก้ไข", "Cameo uses @username directly from Sora app - no conversion needed")}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => window.open("https://apps.apple.com/app/sora/id6738323805", "_blank")}
                        >
                            <ExternalLink className="h-4 w-4" />
                            {t("ดาวน์โหลด Sora App", "Download Sora App")}
                        </Button>
                        <Button onClick={() => setCameoInstructionsOpen(false)}>
                            {t("เข้าใจแล้ว", "Got it")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Cameo Dialog */}
            <Dialog open={addCameoDialogOpen} onOpenChange={setAddCameoDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AtSign className="h-5 w-5 text-pink-500" />
                            {t("เพิ่ม Cameo", "Add Cameo")}
                        </DialogTitle>
                        <DialogDescription>
                            {t("ใส่ @username จากแอพ Sora ของคุณ", "Enter your @username from Sora app")}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t("@username ของคุณ", "Your @username")}</label>
                            <Input
                                placeholder="@yourname"
                                value={newCameoUsername}
                                onChange={(e) => setNewCameoUsername(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && saveCameo()}
                                className="font-mono"
                            />
                            <p className="text-[10px] text-muted-foreground">
                                {t("ใส่ @username ตรงๆ จากแอพ Sora (เช่น @johndoe)", "Enter @username exactly from Sora app (e.g. @johndoe)")}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddCameoDialogOpen(false)}>{t("ยกเลิก", "Cancel")}</Button>
                        <Button onClick={saveCameo} disabled={!newCameoUsername.trim()} className="bg-pink-500 hover:bg-pink-600">
                            <Plus className="h-4 w-4 mr-2" />
                            {t("เพิ่ม Cameo", "Add Cameo")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Error Detail Dialog */}
            <Dialog open={!!selectedErrorLog} onOpenChange={(open) => !open && setSelectedErrorLog(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="h-5 w-5" />
                            {t("สาเหตุที่ล้มเหลว", "Failure Reason")}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedErrorLog && (
                        <p className="text-sm text-red-400">
                            {selectedErrorLog.error_message
                                ? translateErrorMessage(selectedErrorLog.error_message, t("th", "en") === "th")
                                : t("ไม่ทราบสาเหตุ - กรุณาติดต่อแอดมิน", "Unknown reason - please contact admin")}
                        </p>
                    )}
                </DialogContent>
            </Dialog>
        </div >
    );
};

export default Sora;
